import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';
import { ONE_PAGE_SECTIONS } from 'constants/businessToolSections/onePage';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { NumberInputControlled } from 'components/_basic/Form/NumberInput';
import { FormHeader } from 'components/_basic/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import {
  EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES,
  prefix as executiveSummaryPrefix,
} from 'components/BusinessToolForm/Sections/ExecutiveSummary/FinancialInformationExecSummary/utils/form';
import { GLOBAL_FIELD_NAMES, globalPrefix } from 'components/BusinessToolForm/Sections/Global/form';

import { concatWithDot } from 'helpers/string';

export const FinancialProjections: React.FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const canAccess = useCheckSubSectionAccess(ONE_PAGE_SECTIONS.FINANCIAL_PROJECTIONS);

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader locked={!canAccess} title={t('chapters.FINANCIAL_PROJECTIONS')} />
      </FormSectionCard>
      <FormSectionCard>
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12} md={6}>
            <NumberInputControlled
              control={control}
              id="onePage.financialProjection.projectedAnnualIncome"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PROJECTED_ANNUAL_INCOME)}
              label={t('executiveSummary.financialInformation.projectedAnnualIncome')}
              adornmentType="currency"
              disabled={!canAccess}
              defaultValue=""
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <NumberInputControlled
              control={control}
              id="onePage.financialProjection.projectedAnnualProfit"
              name={concatWithDot(
                executiveSummaryPrefix,
                EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES.PROJECTED_ANNUAL_PROFIT,
              )}
              label={t('executiveSummary.financialInformation.projectedAnnualProfit')}
              adornmentType="currency"
              disabled={!canAccess}
              defaultValue=""
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <NumberInputControlled
              control={control}
              id="onePage.financialProjection.forecastedProfitability"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.FORECASTED_PROFITABILITY)}
              label={t('executiveSummary.financialInformation.forecastedProfitability')}
              adornmentType="percentage"
              disabled={!canAccess}
              defaultValue=""
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <NumberInputControlled
              control={control}
              id="onePage.financialProjection.projectedPlayback"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PROJECT_PAYBACK_PERIOD)}
              label={t('executiveSummary.financialInformation.projectedPlayback')}
              disabled={!canAccess}
              maxLength={3}
              defaultValue=""
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <NumberInputControlled
              control={control}
              id="onePage.financialProjection.returnOnInvestment"
              name={concatWithDot(executiveSummaryPrefix, EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES.RETURN_ON_INVESTMENT)}
              label={t('executiveSummary.financialInformation.returnOnInvestment')}
              adornmentType="percentage"
              disabled={!canAccess}
              defaultValue=""
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <NumberInputControlled
              control={control}
              id="onePage.financialProjection.internalRateOfReturn"
              name={concatWithDot(
                executiveSummaryPrefix,
                EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES.INTERNAL_RATE_OF_RETURN,
              )}
              label={t('executiveSummary.financialInformation.internalRateOfReturn')}
              adornmentType="percentage"
              disabled={!canAccess}
              defaultValue=""
            />
          </Grid>
        </Grid>
      </FormSectionCard>
    </>
  );
};
